import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string | null;
  /** Оператор платформы (доступ к /admin). Отсутствует/false у обычных пользователей. */
  isAdmin?: boolean;
}

interface Tokens {
  accessToken: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;

  /** Устанавливает пользователя и access JWT; refresh — в httpOnly cookie (API). */
  setAuth: (user: User, tokens: Tokens) => void;

  /** Обновляет access JWT (после refresh); cookie обновляет сервер. */
  updateTokens: (tokens: Tokens) => void;

  /** Сбрасывает всё состояние авторизации */
  logout: () => void;
}

type PersistedAuthSlice = Pick<AuthState, 'user' | 'accessToken' | 'isAuthenticated'>;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,

      setAuth: (user, tokens) =>
        set({
          user,
          accessToken: tokens.accessToken,
          isAuthenticated: true,
        }),

      updateTokens: (tokens) =>
        set({
          accessToken: tokens.accessToken,
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
        }),
    }),
    {
      name: 'sloy-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
      // sessionStorage: access не переживает закрытие вкладки; refresh — httpOnly cookie.
      storage: {
        getItem: (key) => {
          const value = sessionStorage.getItem(key);
          if (!value) return null;
          try {
            const parsed = JSON.parse(value) as { state?: PersistedAuthSlice & { refreshToken?: unknown }; version?: number };
            if (!parsed || typeof parsed !== 'object' || parsed.state === undefined) {
              return null;
            }
            const state = { ...parsed.state };
            if ('refreshToken' in state) {
              delete state.refreshToken;
            }
            return { state, version: parsed.version ?? 0 };
          } catch {
            return null;
          }
        },
        setItem: (key, value) => sessionStorage.setItem(key, JSON.stringify(value)),
        removeItem: (key) => sessionStorage.removeItem(key),
      },
    },
  ),
);
